import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import type { User } from "firebase/auth";
import { WifiOff } from "lucide-react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import { useDispatch } from "react-redux";
import { auth } from "../api/firebaseApi";
import { SessionAPI } from "../api/sessionApi";
import { ContextProviders } from "../contexts/ContextProviders";
import { lawyerStateFromSession } from "../hooks/useGetLawyerData";
import useDocuments from "../hooks/useDocuments";
import { wasAppInitiatedLogout } from "../hooks/useLogOut";
import PageWrapper from "../pages/PageWrapper";
import { VisaDocumentType } from "../redux/documents/types";
import { setLawyerData } from "../redux/lawyer/lawyerSlice";
import { MixpanelAnalyticsProvider } from "../contexts/MixpanelAnalyticsContext";
import { AgentAttentionProvider } from "../features/agentAttention/context/AgentAttentionProvider";
import { AgentPanelHost } from "../features/agents/components/AgentPanelHost";
import { RunningTurnsWatcher } from "../features/agents/components/RunningTurnsWatcher";
import { NotificationsProvider } from "../features/notifications/NotificationsProvider";
import { PresenceHeartbeat } from "../features/notifications/PresenceHeartbeat";
import { WorkTracker } from "../features/timeTracking/tracker/WorkTracker";
import { RealtimeProvider } from "../lib/realtime/RealtimeProvider";
import { LoadingState } from "./brand";
import { FileExtensionDialog } from "./common/FileExtensionDialog";
import { FileUploadWindow } from "./common/FileUploadWindow";
import UploadExternalLinkModal from "./common/UploadExternalLinkModal";
import { Button } from "./ui/button";
import { EmptyState } from "./ui/empty-state";
import { TooltipProvider } from "./ui/tooltip";

export const ProtectedRoute = () => {
  const { id: uid } = useParams();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const dispatch = useDispatch();

  // Fetch document and update the Redux store
  // This way all the nested routes can access the documents without
  // rendering the DocumentsTable page
  useDocuments([
    {
      path: `/documents/${uid}/docs`,
      documentType: VisaDocumentType.Standard
    },
    {
      path: `/documents/${uid}/signed_expert_letters`,
      documentType: VisaDocumentType.SignedExpertLetters
    },
    {
      path: `/documents/${uid}/evidence_docs`,
      documentType: VisaDocumentType.Evidence
    }
  ]);

  // Firebase clears the user itself when a refresh token is truly invalid, so a
  // token error while a user still exists is a connectivity problem: offer a
  // retry instead of sending them to /login.
  const bootstrap = useCallback(
    async (user: User) => {
      try {
        const token = await user.getIdToken();
        setConnectionError(false);
        setIsAuthenticated(true);
        try {
          const session = await SessionAPI.getMe({ token });
          const lawyer = lawyerStateFromSession(session);
          if (lawyer?.uid === user.uid) {
            dispatch(setLawyerData(lawyer));
          }
        } catch (sessionError) {
          Sentry.captureException(sessionError);
        }
      } catch (error) {
        Sentry.captureException(error);
        setConnectionError(true);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    let hadUser = false;
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        hadUser = true;
        await bootstrap(user);
      } else {
        if (hadUser && !wasAppInitiatedLogout()) {
          Sentry.captureMessage("session_lost", "warning");
        }
        hadUser = false;
        setConnectionError(false);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [bootstrap]);

  const retry = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setIsRetrying(true);
    try {
      await bootstrap(user);
    } finally {
      setIsRetrying(false);
    }
  }, [bootstrap]);

  useEffect(() => {
    if (!connectionError) return undefined;
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [connectionError, retry]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingState size="xl" />
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="flex h-screen">
        <EmptyState
          icon={WifiOff}
          title="Can't Reach CaseBlink"
          description="We couldn't refresh your session. Check your connection and try again. You're still signed in."
          action={
            <Button
              variant="primary"
              onClick={() => void retry()}
              disabled={isRetrying}
            >
              {isRetrying ? "Retrying…" : "Retry"}
            </Button>
          }
        />
      </div>
    );
  }

  // If not authenticated, redirect to the login page
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // If authenticated, render the protected components
  return (
    <ContextProviders>
      <RealtimeProvider>
        <NotificationsProvider>
          <AgentAttentionProvider>
            <TooltipProvider delayDuration={0}>
              <PresenceHeartbeat />
              <WorkTracker />
              <RunningTurnsWatcher />

              <div className="flex h-screen flex-col overflow-hidden">
                <div className="fixed bottom-0 right-8 z-1500 bg-elevated rounded-t-3xl shadow-2xl">
                  <FileUploadWindow id="" path="" />
                  <FileExtensionDialog />
                </div>

                <UploadExternalLinkModal />
                <AgentPanelHost />

                <main className="flex min-h-0 flex-1 flex-col overflow-hidden w-full xl:mx-auto 2x:w-256">
                  <MixpanelAnalyticsProvider>
                    <PageWrapper component={<Outlet />} />
                  </MixpanelAnalyticsProvider>
                </main>
              </div>
            </TooltipProvider>
          </AgentAttentionProvider>
        </NotificationsProvider>
      </RealtimeProvider>
    </ContextProviders>
  );
};
